import { toCamel, toSnake } from "@/utils/transform/text";

/**
 * JsonArray Type Guard function
 */
function isJsonArray(obj: JsonValue): obj is JsonArray {
  return Array.isArray(obj);
}

/**
 * JsonObject Type Guard function
 */
function isJsonObject(obj: JsonValue): obj is JsonObject {
  return obj !== null && typeof obj === "object";
}

/**
 * Converts all keys in an object into camelCase.
 *
 * This will create new copies of nested objects and arrays.
 *
 * Explicit type
 * casting is necessary in this method as returning a new object or array of the
 * same type as the original union type cannot be inferred. Doing so will ensure
 * that a string in is typed as a string out, and something that extends a JSONObject
 * in, will be a generic JSONObject on return. We cannot presume to know the keynames
 * on the return object for typing.
 */
export const keysToCamel = <T extends JsonValue, U extends Extract<JsonValue, T>>(source: T): U => {
  if (isJsonArray(source)) {
    return source.map(keysToCamel) as U;
  } else if (isJsonObject(source)) {
    const newObject: JsonObject = {};

    Object.keys(source).forEach((key) => {
      newObject[toCamel(key)] = keysToCamel(source[key]);
    });

    return newObject as U;
  }

  // Requires multi casting because typescript can't quite infer the correct type in this scenario.
  return source as JsonPrimitive as U;
};

export const keysToSnake = <T extends JsonValue, U extends Extract<JsonValue, T>>(source: T): U => {
  if (isJsonArray(source)) {
    return source.map(keysToSnake) as U;
  } else if (isJsonObject(source)) {
    const newObject: JsonObject = {};

    Object.keys(source).forEach((key) => {
      newObject[toSnake(key)] = keysToSnake(source[key]);
    });

    return newObject as U;
  }

  // Requires multi casting because typescript can't quite infer the correct type in this scenario.
  return source as JsonPrimitive as U;
};

/**
 * Removes duplicates from an array of objects based on a primary key.
 *
 * @param primaryKey The key to check for duplicates.
 * @param array The array of objects to filter.
 * @returns A new array with duplicates removed.
 */
export const uniqBy = <T>(primaryKey: keyof T, array: T[]): T[] => {
  const seen = new Set();
  return array.filter((item) => {
    const value = item[primaryKey];
    if (seen.has(value)) {
      return false;
    }
    seen.add(value);
    return true;
  });
};
